import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-toastify';
import { Box, IconButton, Typography } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getHistoryItems, deleteHistoryItem } from '../../services/api';
import DataTable from '../common/DataTable';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';
import { Image } from 'antd';

const HistoryList = forwardRef(function HistoryList({ onEdit }, ref) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await getHistoryItems();
      const sorted = [...data].sort((a, b) => (a.order || 0) - (b.order || 0));
      setItems(sorted);
    } catch (error) {
      toast.error('Failed to fetch history items');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: fetchItems
  }));

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await deleteHistoryItem(deleteId);
      toast.success('History item deleted successfully');
      fetchItems();
      setDeleteId(null);
    } catch (error) {
      toast.error('Failed to delete history item');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      id: 'imageUrl',
      label: 'IMAGE',
      width: '140px',
      render: (row) => (
        <Image
          src={row.imageUrl}
          alt="History"
          width={100}
          height={60}
          style={{ objectFit: 'cover', borderRadius: '6px' }}
        />
      ),
    },
    {
      id: 'title',
      label: 'HISTORICAL TITLE',
      render: (row) => (
        <Typography variant="body1" sx={{ fontWeight: 600, color: 'var(--header-title)' }}>
          {row.title}
        </Typography>
      )
    },
    { 
      id: 'year', 
      label: 'YEAR',
      width: '100px',
      align: 'center',
      render: (row) => (
        <Typography sx={{ fontWeight: 500, color: 'var(--subtitle-text)' }}>
          {row.year}
        </Typography>
      )
    },
    { 
      id: 'order', 
      label: 'ORDER',
      width: '100px',
      align: 'center'
    },
    {
      id: 'actions',
      label: 'ACTIONS',
      align: 'right',
      width: '120px',
      render: (row) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            onClick={() => onEdit(row)}
            size="small"
            sx={{
              color: '#94a3b8',
              '&:hover': { color: '#2563eb', bgcolor: '#eff6ff' },
              transition: 'all 0.2s'
            }}
          >
            <EditIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            onClick={() => setDeleteId(row.id)}
            size="small"
            sx={{
              color: '#94a3b8',
              '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' },
              transition: 'all 0.2s',
              ml: 1
            }}
          >
            <DeleteIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <DataTable
        premium={true}
        loading={loading}
        data={items}
        columns={columns}
        emptyTitle="No History Items Found"
        emptySubtitle="There are no historical records to display yet."
      />
      <DeleteConfirmDialog
        open={!!deleteId}
        onClose={() => !deleteLoading && setDeleteId(null)}
        onConfirm={confirmDelete}
        itemName="history item"
        loading={deleteLoading}
      />
    </>
  );
});

export default HistoryList;
